import React, { useState, useEffect } from 'react';
import { Modal, Button, CircularProgress, Typography, Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import './PDFPreviewModal.css';

const PDFPreviewModal = ({ 
  isOpen, 
  onClose, 
  onApprove, 
  onReject,
  pdfUrl,
  isGenerating,
  puantajData 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isFetchingPdf, setIsFetchingPdf] = useState(false); // PDF indirme durumu

  useEffect(() => {
    if (isOpen && pdfUrl && !isFetchingPdf && !blobUrl) {
      setIsLoading(false);
      // PDF'i blob olarak indir ve local URL oluştur
      fetchPDFAsBlob(pdfUrl);
    } else if (!isOpen) {
      // Modal kapatıldığında fetch durumunu sıfırla
      setIsFetchingPdf(false);
    }
  }, [isOpen, pdfUrl]); // isFetchingPdf ve blobUrl bağımlılıklarını kaldırdık

  // Modal kapatıldığında state'leri temizle
  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      setIsFetchingPdf(false);
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    }
  }, [isOpen, blobUrl]);

  const fetchPDFAsBlob = async (url) => {
    // Zaten fetch ediliyor veya blob mevcut ise işlem yapma
    if (isFetchingPdf || blobUrl) {
      return;
    }
    
    // Firebase Storage URL'leri için direkt kullan (CORS sorunu nedeniyle)
    if (url && url.includes('firebasestorage.googleapis.com')) {
      console.log('📋 Firebase Storage PDF doğrudan gösteriliyor:', url);
      setBlobUrl(url); // Direkt URL'i kullan, fetch yapmadan
      setIsFetchingPdf(false);
      return;
    }
    
    try {
      setIsFetchingPdf(true);
      
      console.log('📥 PDF blob olarak indiriliyor:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const localUrl = URL.createObjectURL(blob);
      setBlobUrl(localUrl);
      console.log('✅ PDF blob URL oluşturuldu:', localUrl);
    } catch (error) {
      console.error('❌ PDF blob indirme hatası:', error);
      // CORS hatası durumunda direkt URL'i kullan
      if (error.message.includes('Failed to fetch') && url) {
        console.log('🔄 CORS hatası nedeniyle direkt URL kullanılıyor');
        setBlobUrl(url);
      } else {
        setBlobUrl(null);
      }
    } finally {
      setIsFetchingPdf(false);
    }
  };

  // Component unmount edildiğinde cleanup
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, []); // Sadece mount/unmount sırasında çalış

  const handleApprove = async () => {
    setIsLoading(true);
    try {
      await onApprove();
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = () => {
    onReject();
  };

  const handleDownload = () => {
    if (blobUrl) {
      const link = document.createElement('a');
      link.href = blobUrl;
      // Dosya adı: makine kodu-puantaj numarası
      const machineCode = puantajData?.['Makine İsmi'] || 'makine';
      const puantajNumber = puantajData?.['Puantaj Sayısı'] || 'puantaj';
      const contractNumber = `${machineCode}-${puantajNumber}`;
      link.download = `${contractNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="pdf-preview-modal"
      disableEscapeKeyDown
    >
      <div className="pdf-preview-container">
        <div className="pdf-preview-header">
          <Typography variant="h5" component="h2">
            📄 PDF Önizleme - Puantaj Düzenlemesi
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {puantajData?.['Müşteri Adı']} - {puantajData?.['Makine İsmi']}
          </Typography>
        </div>

        <div className="pdf-preview-content">
          {isGenerating ? (
            <div className="pdf-loading">
              <CircularProgress size={60} />
              <Typography variant="h6" style={{ marginTop: 16 }}>
                🔄 PDF oluşturuluyor...
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Lütfen bekleyin, güncellenmiş PDF hazırlanıyor
              </Typography>
            </div>
          ) : isFetchingPdf ? (
            <div className="pdf-loading">
              <CircularProgress size={60} />
              <Typography variant="h6" style={{ marginTop: 16 }}>
                📥 PDF yükleniyor...
              </Typography>
              <Typography variant="body2" color="textSecondary">
                PDF önizleme için hazırlanıyor
              </Typography>
            </div>
          ) : blobUrl ? (
            <div className="pdf-viewer-container">
              <iframe
                src={`${blobUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                title="PDF Önizleme"
                className="pdf-iframe"
                frameBorder="0"
                onError={(e) => console.error('❌ PDF iframe hatası:', e)}
                onLoad={() => console.log('✅ PDF iframe yüklendi')}
              />
              
              <div className="pdf-info-panel">
                <Typography variant="h6" gutterBottom>
                  📋 Değişiklik Özeti
                </Typography>
                
                <div className="change-summary">
                  <Typography variant="body2">
                    <strong>Müşteri:</strong> {puantajData?.['Müşteri Adı'] || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Makine:</strong> {puantajData?.['Makine İsmi'] || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Operatör:</strong> {puantajData?.['Operatör Adı'] || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tarih:</strong> {puantajData?.['Tarih'] || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Çalışma Saatleri:</strong> 
                    {puantajData?.['1. Başlangıç Saati']} - {puantajData?.['1. Bitiş Saati']}
                    {puantajData?.['2. Başlangıç Saati'] && (
                      <>, {puantajData['2. Başlangıç Saati']} - {puantajData['2. Bitiş Saati']}</>
                    )}
                  </Typography>
                </div>

                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handleDownload}
                  fullWidth
                  style={{ marginTop: 16 }}
                >
                  PDF'i İndir
                </Button>
              </div>
            </div>
          ) : (
            <div className="pdf-error">
              <Typography variant="h6" color="error">
                ❌ PDF yüklenemedi
              </Typography>
              <Typography variant="body2">
                PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.
              </Typography>
            </div>
          )}
        </div>

        <div className="pdf-preview-actions">
          <Button
            variant="outlined"
            onClick={handleReject}
            startIcon={<ArrowBackIcon />}
            disabled={isLoading || isGenerating || isFetchingPdf}
            size="large"
          >
            Geri Dön
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleApprove}
            startIcon={<CheckIcon />}
            disabled={isLoading || isGenerating || isFetchingPdf || !blobUrl}
            size="large"
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8 }} />
                Kaydediliyor...
              </>
            ) : (
              'Onayla ve Kaydet'
            )}
          </Button>
        </div>

        <div className="pdf-preview-warning">
          <Typography variant="caption" color="textSecondary">
            ⚠️ Onayladığınızda eski puantaj silinecek ve yerine bu güncellenmiş versiyon kaydedilecektir.
          </Typography>
        </div>
      </div>
    </Modal>
  );
};

export default PDFPreviewModal; 